'use client';
/* eslint-disable @next/next/no-img-element */
import { Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import customToaster from '@/components/toast/CustomToaster';
import { useForm } from 'react-hook-form';
import { triggerEvent } from '@/lib/events';
import { useSpinnerContext } from '@/context/SpinnerContext';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useRouter } from 'next/navigation';
import { useShoppingCart } from '@/context/ShoppingCartContext';
import GrazingAngleFields from '../configModalsContent/grazingAngleFields';
import CaptureMethod from '../configModalsContent/captureMethod';
import SelectCaptureConfig from '../configModalsContent/selectCaptureConfig';
import ConfigSettings from '../configModalsContent/configSettings';
import { configFlowDefaultValues, isPlatformExplore, isPlatformHub } from '@/constants';
import { useTranslation } from 'react-i18next';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEventHub } from '@/services/eventEnums';
import {
  productLibraryResolutionToTaskingResolution,
  replaceSpacesWithUnderscore,
} from '@/lib/utils';

interface GrazingAngle {
  min: number;
  max: number;
}

interface ConfigurationSettingsData {
  id: string;
  name: string;
  bitDepth?: string;
  projections?: string;
  radioMetricProcessing?: string;
  grazingAngle?: GrazingAngle;
  polarization?: string;
}

interface ConfigurationDataOptions {
  projection: string[];
  bitdepth: string[];
  radiometric_processing: string[];
  product_type: string[];
}

interface ConfigurationOption {
  name: string;
  value: string;
}

interface ConfigFlow {
  open: boolean;
  grazingCheck: boolean;
  skipConfig: boolean;
  captureConfig: boolean;
  selectCapture: boolean;
  config: boolean;
  thirdFlow: boolean;
}
interface Configuration {
  name: string;
  id: string;
  parameters: Record<string, any>;
  properties?: {
    data?: any;
    bands?: string[];
  };
}
interface Offering {
  offering_id: string;
  offering_name: string;
  configurations: Configuration[];
  price?: number;
  minimum_billable_aoi_size?: number;
  maximum_platform_aoi_size?: number;
  on_platform: boolean;
  supported_applications?: string[];
  sensor_type?: string;
  purchase_options?: any;
  priority?: string;
}

interface ProviderModalProps {
  data: {
    partner_name: string;
    product_name: string;
    product_id: string;
    resolution?: number;
    sensor_type?: string;
    sensor_mode?: string;
  };
  configurations: Array<{
    id: string;
    name: string;
    parameters?: {
      projection?: string[];
      bitdepth?: string[];
      radiometric_processing?: string[];
      product_type?: string[];
    };
    properties?: {
      bands?: string[];
    };
  }>;
  isProductOrderAbleForExplore: boolean;
  isProductOrderAbleForHub: boolean;
  type: string;
  offeringData: Offering | null;
}

export default function ProductLibraryModal({
  data,
  configurations,
  type,
  offeringData,
  isProductOrderAbleForExplore,
  isProductOrderAbleForHub,
}: ProviderModalProps) {
  const [configurationSelect, setConfigurationSelect] = useState<string>('');
  const [configurationOptions, setConfigurationOptions] = useState<ConfigurationOption[]>([]);
  const [configurationDataOptions, setConfigurationDataOptions] =
    useState<ConfigurationDataOptions>({
      projection: [],
      bitdepth: [],
      radiometric_processing: [],
      product_type: [],
    });
  const { startLoaderEvent } = useSpinnerContext();
  const { t } = useTranslation('provider');
  const router = useRouter();
  const { setIsNew, feasibilityData, tabValue } = useShoppingCart();
  const [configurationSettingsData, setConfigurationSettingsData] =
    useState<ConfigurationSettingsData>({
      id: '',
      name: '',
      bitDepth: '',
      projections: '',
      radioMetricProcessing: '',
      grazingAngle: {
        min: 30,
        max: 70,
      },
      polarization: '',
    });

  const [configFlow, setConfigFlow] = useState<ConfigFlow>(configFlowDefaultValues);
  const { open, grazingCheck, skipConfig, captureConfig, selectCapture, config, thirdFlow } =
    configFlow;

  const {
    reset,
    formState: { errors },
  } = useForm();

  const handleClickOpen = (): void => {
    if (isPlatformExplore && !isProductOrderAbleForExplore) {
      window.open(process.env.NEXT_PUBLIC_HUB_TRIAL_URL, '_blank');
      MixpanelTracking.getInstance().track(
        MixpanelEventHub.PROVIDER_PRODUCT_REQUEST_HUB_TRIAL_CLICK,
        {
          product_name: `${data?.partner_name} - ${data?.product_name}`,
        }
      );
      return;
    }
    if (
      (isPlatformExplore && isProductOrderAbleForExplore) ||
      (isPlatformHub && isProductOrderAbleForHub)
    ) {
      if (type === 'Tasking') {
        let url = '';
        const tab = 'tasking';
        const isOptical = data?.sensor_type === 'Optical';
        const isPleiadesNeo = data?.product_name === 'Pleiades-Neo';
        const isGuaranteed = offeringData?.priority === 'guaranteed';
        const dataType = data?.sensor_type ? data?.sensor_type?.toLowerCase() : '';
        const taskingPriority = isOptical ? offeringData?.priority : '';
        const isStandard = offeringData?.priority === 'standard';
        const sensorMode = isPlatformHub && isStandard && isOptical ? data?.sensor_mode : '';
        const source = data?.product_name;
        const resolutionOption = productLibraryResolutionToTaskingResolution(data?.resolution);
        const priorityDayValue =
          isOptical && isPleiadesNeo && isGuaranteed
            ? offeringData?.offering_id === 'aaff4981-5b33-48f8-9d44-5af7695e0af8'
              ? 'one_day'
              : offeringData?.offering_id === '254a664c-7ce5-4a85-ae54-c9cbb8acc447'
                ? 'one_now_weather'
                : null
            : null;

        const bandsOptionValue = offeringData?.purchase_options?.band_options?.[0]?.[0];

        const looksOptionValue = offeringData?.purchase_options?.look_options?.[0]?.[0];

        const demOptionValue = offeringData?.purchase_options?.dem_option?.[0]?.[0]
          ? replaceSpacesWithUnderscore(offeringData?.purchase_options?.dem_option?.[0])
          : '';

        url = `tab=${tab}&data_type=${dataType}${taskingPriority ? `&tasking_priority=${taskingPriority}` : ''}${sensorMode ? `&sensor_mode=${sensorMode}` : ''}&resolution_text=${resolutionOption}&source=${source}${bandsOptionValue ? `&bands=${bandsOptionValue}` : ''}${demOptionValue ? `&dem=${demOptionValue}` : ''}${looksOptionValue ? `&looks=${looksOptionValue}` : ''}${priorityDayValue ? `&priority_day=${priorityDayValue}` : ''}`;

        const taskingUrl = `/?${url}`;

        router.push(taskingUrl);
      } else {
        setIsNew('1');
        router.push('/');
      }
      MixpanelTracking.getInstance().track(MixpanelEventHub.PROVIDER_PRODUCT_ORDER_NOW_CLICK, {
        product_name: `${data?.partner_name} - ${data?.product_name}`,
      });
      return;
    }
    if (['UMBRA', 'CAPELLASPACE'].includes(data?.partner_name)) {
      setConfigFlow((prev: ConfigFlow) => ({
        ...prev,
        grazingCheck: true,
        open: true,
      }));
    } else if (
      offeringData &&
      !offeringData?.on_platform &&
      configurations?.length === 1 &&
      configurations?.[0]?.name === 'Default Configuration'
    ) {
      setConfigFlow((prev: ConfigFlow) => ({
        ...prev,
        skipConfig: true,
        open: true,
      }));
    } else if (
      offeringData &&
      offeringData?.on_platform &&
      configurations?.length === 1 &&
      configurations?.[0]?.name === 'Default Configuration'
    ) {
      triggerEvent(startLoaderEvent);
      if (type === 'Tasking') {
        tabValue.current = '2';
        setIsNew('2');
        feasibilityData.current = {
          resolution: data?.resolution,
          source: data?.product_name,
        };
      }
      router.push('/');
    } else if (offeringData && offeringData?.on_platform && configurations?.length > 1) {
      setConfigFlow((prev: ConfigFlow) => ({
        ...prev,
        captureConfig: true,
        thirdFlow: true,
        open: true,
      }));
    } else if (offeringData && !offeringData?.on_platform && configurations?.length > 1) {
      setConfigFlow((prev: ConfigFlow) => ({
        ...prev,
        selectCapture: true,
        open: true,
      }));
    }
  };

  const handleClose = (): void => {
    setConfigFlow(configFlowDefaultValues);
    setConfigurationSettingsData({
      id: '',
      name: '',
      bitDepth: '',
      projections: '',
      radioMetricProcessing: '',
      grazingAngle: {
        min: 30,
        max: 70,
      },
      polarization: '',
    });
    setConfigurationSelect('');
    reset();
    MixpanelTracking.getInstance().track(
      MixpanelEventHub.PROVIDER_PRODUCT_ORDER_DATA_TYPE_CLOSE_POPUP
    );
  };

  const handleBack = (): void => {
    if (config && thirdFlow) {
      setConfigFlow((prev: ConfigFlow) => ({
        ...prev,
        config: false,
        captureConfig: true,
      }));
      return;
    }
    if (config) {
      setConfigFlow((prev: ConfigFlow) => ({
        ...prev,
        config: false,
        selectCapture: true,
      }));
      return;
    }
    setConfigFlow((prev: ConfigFlow) => ({
      ...prev,
      open: false,
    }));
    MixpanelTracking.getInstance().track(
      MixpanelEventHub.PROVIDER_PRODUCT_ORDER_DATA_TYPE_CONFIGURATION_BACK_CLICK
    );
  };

  const handleGrazingCheck = (): void => {
    if (!configurationSettingsData?.polarization) {
      return customToaster('Please filled all input fields to continue!!');
    }
    feasibilityData.current = {
      satellite_constellation: `${data?.product_name} - ${type} - ${offeringData?.offering_name}`,
      image_type_and_resolution: {
        name: configurationSettingsData?.name,
        parameters: {
          grazingAngle: {
            min: configurationSettingsData?.grazingAngle?.min,
            max: configurationSettingsData?.grazingAngle?.max,
          },
          polarization: configurationSettingsData?.polarization,
        },
      },
    };
    triggerEvent(startLoaderEvent);
    router.push(`/product-library/${data?.product_id}/feasibility-request`);
  };
  const handleSkipConfig = (): void => {
    triggerEvent(startLoaderEvent);
    feasibilityData.current = {
      image_type_and_resolution: { name: 'Default Configuration' },
      satellite_constellation: `${data?.product_name} - ${type} - ${offeringData?.offering_name}`,
    };
    router.push(`/product-library/${data?.product_id}/feasibility-request`);
  };
  const handleCaptureConfig = (): void => {
    if (configurationSelect !== '') {
      if (configurationSelect === 'SkyWatch Standard') {
        triggerEvent(startLoaderEvent);
        if (type === 'Tasking') {
          tabValue.current = '2';
          setIsNew('2');
          feasibilityData.current = {
            resolution: data?.resolution,
            source: data?.product_name,
          };
        }
        MixpanelTracking.getInstance().track(
          MixpanelEventHub.PROVIDER_PRODUCT_ORDER_DATA_TYPE_CONFIGURATION_NEXT_CLICK,
          {
            capture_method: type,
            product_type: data?.sensor_type,
            configuration: configurationSelect,
          }
        );
        router.push('/');
        return;
      } else if (configurationSelect === 'Alternate Configurations') {
        setConfigFlow(prev => ({
          ...prev,
          captureConfig: false,
        }));

        const filteredConfigurations = configurations
          ?.filter((item: any) => item?.name !== 'Default Configuration')
          ?.map((item: any) => ({
            name: item?.name,
            value: item?.id,
          }));
        setConfigurationOptions(filteredConfigurations || []);
        setConfigFlow(prev => ({
          ...prev,
          config: true,
        }));

        return;
      }
    } else if (configurationSelect === '') {
      return customToaster('Please select configuration!');
    }
  };
  const handleConfig = (): void => {
    if (
      (configurationOptions?.length > 0 && !configurationSettingsData?.name) ||
      (configurationDataOptions?.bitdepth?.length && !configurationSettingsData?.bitDepth) ||
      (configurationDataOptions?.projection?.length && !configurationSettingsData?.projections) ||
      (configurationDataOptions?.radiometric_processing?.length &&
        !configurationSettingsData?.radioMetricProcessing)
    ) {
      return customToaster('Please filled all input fields to continue!!');
    }

    triggerEvent(startLoaderEvent);
    const feasibilityPayload: any = {
      image_type_and_resolution: {
        name: configurationSettingsData?.name,
        parameters: {
          ...(configurationDataOptions?.projection?.length && {
            projection: [configurationSettingsData?.projections].filter(Boolean) as string[],
          }),
          ...(configurationDataOptions?.bitdepth?.length && {
            bitdepth: [configurationSettingsData?.bitDepth].filter(Boolean) as string[],
          }),
          ...(configurationDataOptions?.radiometric_processing?.length && {
            radiometric_processing: [configurationSettingsData?.radioMetricProcessing].filter(
              Boolean
            ) as string[],
          }),
          ...(configurationSettingsData?.grazingAngle && {
            grazingAngle: configurationSettingsData?.grazingAngle,
          }),
          ...(configurationSettingsData?.polarization && {
            polarization: configurationSettingsData?.polarization,
          }),
        },
      },
      satellite_constellation: `${data?.product_name} - ${type} - ${offeringData?.offering_name}`,
    };

    feasibilityData.current = feasibilityPayload;
    MixpanelTracking.getInstance().track(
      MixpanelEventHub.PROVIDER_PRODUCT_DATA_TYPE_ALTERNATE_CONFIGURATION_NEXT_CLICK,
      {
        bit_depth: configurationSettingsData?.bitDepth,
        projection: configurationSettingsData?.projections,
        radiometric: configurationSettingsData?.radioMetricProcessing,
      }
    );
    router.push(`/product-library/${data?.product_id}/feasibility-request`);
  };
  const handleNext = (): void => {
    if (grazingCheck) {
      handleGrazingCheck();
    }
    if (skipConfig) {
      handleSkipConfig();
    }
    if (captureConfig) {
      handleCaptureConfig();
    }
    if (selectCapture) {
      const filteredConfigurations = configurations
        ?.filter((item: any) => item?.name !== 'Default Configuration')
        ?.map((item: any) => ({
          name: item?.name,
          value: item?.id,
        }));
      setConfigurationOptions(filteredConfigurations);
      setConfigFlow(prev => ({
        ...prev,
        selectCapture: false,
        config: true,
      }));
    }
    if (config) {
      handleConfig();
    }
  };
  return (
    <>
      <Button
        name="order-now-button"
        fullWidth
        aria-label="order-now-button"
        className="p-library-btn"
        variant="contained"
        startIcon={
          ((isPlatformExplore && isProductOrderAbleForExplore) || isPlatformHub) && (
            <i className="icon-shopping-cart"></i>
          )
        }
        sx={{ marginTop: '20px' }}
        onClick={handleClickOpen}
      >
        {isPlatformExplore && !isProductOrderAbleForExplore ? t('requestHubTrial') : t('orderNow')}
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="skywatch-dialog"
        PaperProps={{
          sx: {
            maxWidth: '360px !important',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          <Stack direction="row" justifyContent="space-between" gap="2px">
            <Button
              name="back-button"
              onClick={() => handleBack()}
              color="secondary"
              size="small"
              variant="text"
              startIcon={<ArrowBackIosIcon />}
            >
              {t('common:back')}
            </Button>
            <Button
              name="next-button"
              onClick={() => {
                handleNext();
              }}
              color="secondary"
              size="small"
              variant="text"
              endIcon={<ArrowForwardIosIcon />}
            >
              {t('common:next')}
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent className="thm-dialog-content pt-0">
          <Typography variant="h6" gutterBottom>
            {config ? t('chooseConfiguration') : t('pleaseSelect')}
          </Typography>

          {grazingCheck && (
            <GrazingAngleFields
              configurationSettingsData={configurationSettingsData}
              setConfigurationSettingsData={setConfigurationSettingsData}
            />
          )}
          {(skipConfig || selectCapture) && <CaptureMethod type={type} />}
          {captureConfig && (
            <SelectCaptureConfig
              type={type}
              configurationSelect={configurationSelect}
              setConfigurationSelect={setConfigurationSelect}
            />
          )}

          {config && (
            <ConfigSettings
              configurationDataOptions={configurationDataOptions}
              configurationOptions={configurationOptions}
              configurationSettingsData={configurationSettingsData}
              configurations={configurations}
              setConfigurationDataOptions={setConfigurationDataOptions}
              setConfigurationSettingsData={setConfigurationSettingsData}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
